import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import { v4 as uuidv4 } from 'uuid';

import { orderNonFoodPhysicalPackaging } from 'pages/new-evaluation-form/utils/orderEvalFormFields';

import {
  isFoodForm,
  isNonFoodForm,
  isMultiPackFoodForm,
} from 'pages/new-evaluation-form/utils/checkTypeEvaluationForm';

const COUNTRY_OF_ORIGIN = 'countryOfOrigin';

export const mappingValueEvaluationForm = ({
  isDatetime,
  isNumberType,
  value,
}) => {
  let mappingValue = null;

  if (isDatetime) {
    const isMomentType = moment.isMoment(value); // prevent bad data
    mappingValue = value && isMomentType ? value.format('MM/DD/YYYY') : '';
  } else if (isNumberType) {
    mappingValue = value.toFixed(2);
  } else if (typeof value === 'string') {
    mappingValue = value ? value.replace(/(?:\r\n|\r|\n)/g, '<br />') : '';
  } else {
    mappingValue = value;
  }

  return mappingValue;
};

export const generateDataEachTable = (table = {}) => {
  let result = [];

  if (table) {
    for (const [name, value] of Object.entries(table)) {
      if (name != 'title') {
        const rowData = {
          key: uuidv4(),
          headerName: name,
          property: {
            name,
          },
          ...value,
        };
        result.push(rowData);
      }
      else {
        const rowData = {
          key: uuidv4(),
          headerName: name,
          property: {
            name,
          },
          value,
        };
        result.push(rowData);
      }
    }
  }

  return result;
};

const mappingAllergensTable = (allergenDetails = []) => {
  if (allergenDetails?.length > 0) {
    return allergenDetails.map((item) => {
      const key = uuidv4();
      return {
        ...item,
        key: uuidv4(),
        property: {
          name: key,
        },
      };
    });
  }
  return [];
};

export const transformInitialEvaluationValuesFood = (
  initialValues,
  formAssetDetails
) => {
  let result = {};

  const productEvaForm = initialValues?.productEvaForm?.[0] ?? {};
  const mappingFormAssetDetails =
    transformEvalFormAssetDetails(formAssetDetails);

  const headerTable = [
    {
      key: uuidv4(),
      property: {
        name: 'productName',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.productName,
      competitiveBrand: productEvaForm?.competitiveBrand?.productName,
    },
    {
      key: uuidv4(),
      property: {
        name: 'upc',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.upc,
      competitiveBrand: productEvaForm?.competitiveBrand?.upc,
    },
    {
      key: uuidv4(),
      property: {
        name: 'batchLotCode',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.batchLotCode,
      competitiveBrand: productEvaForm?.competitiveBrand?.batchLotCode,
    },
    {
      key: uuidv4(),
      property: {
        name: 'overallOrganolepticScore',
      },
      privateBrand:
        productEvaForm?.privateBrandProduct?.overallOrganolepticScore ?? 0,
      competitiveBrand:
        productEvaForm?.competitiveBrand?.overallOrganolepticScore ?? 0,
    },
  ];

  const headerResult = [
    {
      key: uuidv4(),
      overallResult: initialValues?.overallResult,
      majorNonConformances: initialValues?.majorNonConformances,
      reviewer: initialValues?.reviewer,
      evaluationType: initialValues?.evaluationType,
      billingComments: initialValues?.billingComments,
      property: {
        name: 'headerResult',
      },
    },
  ];
  const headerImage = [
    {
      key: uuidv4(),
      privateProductFont:
        mappingFormAssetDetails[
        productEvaForm?.privateBrandProduct?.digitalAssetFrontId
        ] ?? null,
      competitiveProductFront:
        mappingFormAssetDetails[
        productEvaForm?.competitiveBrand?.digitalAssetFrontId
        ] ?? null,
    },
  ];

  const organolepticEvaluationSummary = generateDataEachTable(
    productEvaForm?.organolepticEvaluationSummary
  );

  const sampleInformation = generateDataEachTable(
    initialValues?.sampleInformation
  );

  const dietaryCertification = generateDataEachTable(
    initialValues?.dietaryCertification
  );

  const statement = generateDataEachTable(initialValues?.statement);

  const allergenDetails = mappingAllergensTable(
    productEvaForm?.allergenDetails
  );

  const physicalPackagingEvaluation = generateDataEachTable(
    transformInitialPhysicalPackagingTable(
      initialValues?.physicalPackagingEvaluation
    )
  );

  const analyticalTestingProduct = generateDataEachTable(
    productEvaForm?.analyticalTestingProduct?.analyticalTestingProductDetails
  );

  const microbiologicalTesting = generateDataEachTable(
    productEvaForm?.microbiologicalTesting
  );

  const productImagery = [
    transformInitialProductImagery(
      productEvaForm?.qaEvaluationFormAttachment,
      mappingFormAssetDetails
    ) ?? [],
  ];

  result = {
    ...result,
    headerTable,
    headerResult,
    headerImage,
    organolepticEvaluationSummary,
    sampleInformation,
    dietaryCertification,
    statement,
    allergenDetails,
    physicalPackagingEvaluation,
    analyticalTestingProduct,
    microbiologicalTesting,
    productImagery,
    qaEvaluationFormAssetDetails: transformEvalFormAssetDetails(
      formAssetDetails ?? []
    ),
  };

  return result;
};

export const transformInitialEvaluationValuesNonFood = (
  initialValues,
  formAssetDetails
) => {
  let result = {};

  const productEvaForm = initialValues?.productEvaForm?.[0] ?? {};
  const mappingFormAssetDetails =
    transformEvalFormAssetDetails(formAssetDetails);

  const headerTable = [
    {
      key: uuidv4(),
      property: {
        name: 'productName',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.productName,
      competitiveBrand: productEvaForm?.competitiveBrand?.productName,
    },
    {
      key: uuidv4(),
      property: {
        name: 'upc',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.upc,
      competitiveBrand: productEvaForm?.competitiveBrand?.upc,
    },
    {
      key: uuidv4(),
      property: {
        name: 'batchLotCode',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.batchLotCode,
      competitiveBrand: productEvaForm?.competitiveBrand?.batchLotCode,
    },
  ];

  const headerResult = [
    {
      key: uuidv4(),
      overallResult: initialValues?.overallResult,
      majorNonConformances: initialValues?.majorNonConformances,
      reviewer: initialValues?.reviewer,
      evaluationType: initialValues?.evaluationType,
      billingComments: initialValues?.billingComments,
      property: {
        name: 'headerResult',
      },
    },
  ];
  const headerImage = [
    {
      key: uuidv4(),
      privateProductFont:
        mappingFormAssetDetails[
        productEvaForm?.privateBrandProduct?.digitalAssetFrontId
        ] ?? null,
      competitiveProductFront:
        mappingFormAssetDetails[
        productEvaForm?.competitiveBrand?.digitalAssetFrontId
        ] ?? null,
    },
  ];

  const sensoryEvaluationSummary = generateDataEachTable(
    productEvaForm?.sensoryEvaluationSummary
  );

  const sampleInformation = generateDataEachTable(
    initialValues?.sampleInformation
  );

  const allergenDetails = mappingAllergensTable(
    productEvaForm?.allergenDetails
  );

  const physicalPackagingEvaluation = generateDataEachTable(
    orderNonFoodPhysicalPackaging(
      transformInitialPhysicalPackagingTable(
        initialValues?.physicalPackagingEvaluation
      )
    )
  );

  const analyticalTestingProduct = generateDataEachTable(
    productEvaForm?.analyticalTestingProduct?.analyticalTestingProductDetails
  );

  const microbiologicalTesting = generateDataEachTable(
    productEvaForm?.microbiologicalTesting
  );

  const productImagery = [
    transformInitialProductImagery(
      productEvaForm?.qaEvaluationFormAttachment,
      mappingFormAssetDetails
    ) ?? [],
  ];

  result = {
    ...result,
    headerTable,
    headerResult,
    headerImage,
    sensoryEvaluationSummary,
    sampleInformation,
    allergenDetails,
    physicalPackagingEvaluation,
    analyticalTestingProduct,
    microbiologicalTesting,
    productImagery,
    qaEvaluationFormAssetDetails: transformEvalFormAssetDetails(
      formAssetDetails ?? []
    ),
  };

  return result;
};

// Helper function to generate headerTable for multipack
export function generateMultipackHeaderTable(productEvaForm) {
  return [
    {
      key: uuidv4(),
      property: {
        name: 'productName',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.productName,
      competitiveBrand: productEvaForm?.competitiveBrand?.productName,
    },
    {
      key: uuidv4(),
      property: {
        name: 'upc',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.upc,
      competitiveBrand: productEvaForm?.competitiveBrand?.upc,
    },
    {
      key: uuidv4(),
      property: {
        name: 'batchLotCode',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.batchLotCode,
      competitiveBrand: productEvaForm?.competitiveBrand?.batchLotCode,
    },
    {
      key: uuidv4(),
      property: {
        name: 'overallOrganolepticScore',
      },
      privateBrand:
        productEvaForm?.privateBrandProduct?.overallOrganolepticScore ?? 0,
      competitiveBrand:
        productEvaForm?.competitiveBrand?.overallOrganolepticScore ?? 0,
    },
  ];
}

// Helper function to generate headerImage for multipack
export function generateMultiPackHeaderImage(productEvaForm, formAssetDetails) {
  const mappingFormAssetDetails =
    transformEvalFormAssetDetails(formAssetDetails);
  return [
    {
      key: uuidv4(),
      privateProductFont:
        mappingFormAssetDetails[
        productEvaForm?.privateBrandProduct?.digitalAssetFrontId
        ] ?? null,
      competitiveProductFront:
        mappingFormAssetDetails[
        productEvaForm?.competitiveBrand?.digitalAssetFrontId
        ] ?? null,
    },
  ];
}


export const transformInitialEvaluationValuesMultiPack = (
  initialValues,
  formAssetDetails
) => {
  let result = {};
  const mappingFormAssetDetails = transformEvalFormAssetDetails(formAssetDetails);
  const productEvaForms = initialValues?.productEvaForm ?? [];

  // Map each productEvaForm to its own set of table data
  const headerTable = productEvaForms.map(form => generateMultipackHeaderTable(form));
  const headerImage = productEvaForms.map(form => generateMultiPackHeaderImage(form, formAssetDetails));

  const organolepticEvaluationSummary = productEvaForms.map(form => generateDataEachTable(form?.organolepticEvaluationSummary));
  //const allergenDetails = productEvaForms.map(form => mappingAllergensTable(form?.allergenDetails));
  const allergenDetails = productEvaForms.map(form => generateDataEachTable(form?.allergenPbCbProduct));

  //const analyticalTestingProduct = productEvaForms.map(form => generateDataEachTable(form?.analyticalTestingProduct?.analyticalTestingProductDetails));

  let analyticalTestingProduct = productEvaForms.map((form) => {
    let resultAnalyticalTestingProduct = [];
    const rowData = {
      key: uuidv4(),
      headerName: 'title',
      property: {
        name: 'title',
      },
      value: form?.analyticalTestingProduct?.title,
    };
    resultAnalyticalTestingProduct.push(rowData);
    resultAnalyticalTestingProduct = resultAnalyticalTestingProduct.concat(generateDataEachTable(form?.analyticalTestingProduct?.analyticalTestingProductDetails));
    return resultAnalyticalTestingProduct;
  });

  const microbiologicalTesting = productEvaForms.map(form => generateDataEachTable(form?.microbiologicalTesting));
  //const productImagery = productEvaForms.map(form => transformInitialProductImagery(form?.qaEvaluationFormAttachment, mappingFormAssetDetails) ?? {});
  const productImagery = [transformInitialProductImagery(productEvaForms?.[0]?.qaEvaluationFormAttachment, mappingFormAssetDetails) ?? [],];
  // These are not per-product, so keep as single arrays
  const headerResult = [
    {
      key: uuidv4(),
      overallResult: initialValues?.overallResult,
      majorNonConformances: initialValues?.majorNonConformances,
      reviewer: initialValues?.reviewer,
      evaluationType: initialValues?.evaluationType,
      billingComments: initialValues?.billingComments,
      property: {
        name: 'headerResult',
      },
    },
  ];
  const sampleInformation = generateDataEachTable(initialValues?.sampleInformation);
  const dietaryCertification = generateDataEachTable(initialValues?.dietaryCertification);
  const statement = generateDataEachTable(initialValues?.statement);
  const physicalPackagingEvaluation = generateDataEachTable(
    transformInitialPhysicalPackagingTable(initialValues?.physicalPackagingEvaluation)
  );

  result = {
    headerTable,
    headerResult,
    headerImage,
    organolepticEvaluationSummary,
    sampleInformation,
    dietaryCertification,
    statement,
    allergenDetails,
    physicalPackagingEvaluation,
    analyticalTestingProduct,
    microbiologicalTesting,
    productImagery,
    qaEvaluationFormAssetDetails: mappingFormAssetDetails,
    productEvalForms: productEvaForms,
  };

  return result;
};

export const transformEvalFormAssetDetails = (qaEvaluationFormAssetDetails) => {
  let result = {};

  if (qaEvaluationFormAssetDetails?.length > 0) {
    qaEvaluationFormAssetDetails.forEach((assetDetail) => {
      const { qaEvaluationFormAssetId } = assetDetail;

      result = {
        ...result,
        [qaEvaluationFormAssetId]: assetDetail,
      };
    });
  }

  return result;
};

const transformInitialEachPhotoTypeProduct = ({
  photo,
  mappingFormAssetDetails,
}) => {
  let result = {};

  if (isEmpty(photo)) return {};

  for (const imageItem of photo) {
    result = {
      ...result,
      [imageItem.index]: {
        ...imageItem,
        digitalAssetId: mappingFormAssetDetails[imageItem.digitalAssetId],
      },
    };
  }

  return result;
};

export const transformInitialProductImagery = (
  productImagery,
  mappingFormAssetDetails
) => {
  const privateBrandProduct = transformInitialEachPhotoTypeProduct({
    photo: productImagery?.privateBrandProduct,
    mappingFormAssetDetails,
  });

  const competitiveBrandProduct = transformInitialEachPhotoTypeProduct({
    photo: productImagery?.competitiveBrandProduct,
    mappingFormAssetDetails,
  });

  const comparativeAttachment = transformInitialEachPhotoTypeProduct({
    photo: productImagery?.comparativeAttachment,
    mappingFormAssetDetails,
  });

  return {
    privateBrandProduct,
    competitiveBrandProduct,
    comparativeAttachment,
  };
};

export const generateBrandTypeProduct = (headerTable, headerImage) => {
  let privateBrandProduct = {};
  let competitiveBrandProduct = {};

  const fields = [
    'productName',
    'upc',
    'batchLotCode',
    'overallOrganolepticScore',
  ];

  fields.forEach((field) => {
    const foundRow = headerTable.find(
      (row) => row?.property?.name?.toLowerCase() === field.toLowerCase()
    );

    if (foundRow) {
      privateBrandProduct = {
        ...privateBrandProduct,
        [field]: foundRow.privateBrand,
      };
      competitiveBrandProduct = {
        ...competitiveBrandProduct,
        [field]: foundRow.competitiveBrand,
      };
    }
  });

  return {
    privateBrandProduct: {
      ...privateBrandProduct,
      digitalAssetFrontId:
        headerImage?.[0]?.privateProductFont?.qaEvaluationFormAssetId ?? null,
    },
    competitiveBrandProduct: {
      ...competitiveBrandProduct,
      digitalAssetFrontId:
        headerImage?.[0]?.competitiveProductFront?.qaEvaluationFormAssetId ??
        null,
    },
  };
};

export const generateParamsForEachTable = (tableFormValues) => {
  let result = {};

  if (tableFormValues) {
    tableFormValues.forEach((formItem) => {
      const { property: { name }, value } = formItem;
      if (name != 'title') {
        const {
          property: { name },
          ...restValues
        } = formItem;
        result = {
          ...result,
          [name]: {
            ...restValues,
          },
        };
      } else {
        result = {
          ...result,
          [name]: value,
        };
      }
    });
  }

  return result;
};

const transformEachPhotoTypeBrand = (photo) => {
  if (isEmpty(photo)) return [];

  return photo.map((photo) => ({
    ...photo,
    digitalAssetId: photo?.digitalAssetId?.qaEvaluationFormAssetId ?? null,
  }));
};

const transformProductImageryParams = (productImagery) => {
  const privateBrandProduct = transformEachPhotoTypeBrand(
    Object.values(productImagery?.privateBrandProduct)
  );
  const competitiveBrandProduct = transformEachPhotoTypeBrand(
    Object.values(productImagery?.competitiveBrandProduct)
  );

  const comparativeAttachment = transformEachPhotoTypeBrand(
    Object.values(productImagery?.comparativeAttachment)
  );

  return {
    privateBrandProduct,
    competitiveBrandProduct,
    comparativeAttachment,
  };
};

const transformParamsEvalFoodForm = (formValues) => {

  let result = {};
  let organolepticSummaryInitial = JSON.parse(JSON.stringify(formValues?.organolepticEvaluationSummary));
  organolepticSummaryInitial = updateOrganolepticSummaryScores(organolepticSummaryInitial);

  const { privateBrandProduct, competitiveBrandProduct: competitiveBrand } =
    generateBrandTypeProduct(formValues?.headerTable, formValues?.headerImage);

  result = {
    ...result,
    overallResult: formValues?.headerResult?.[0]?.overallResult,
    majorNonConformances: formValues?.headerResult?.[0]?.majorNonConformances,
    reviewer: formValues?.headerResult?.[0]?.reviewer,
    evaluationType: formValues?.headerResult?.[0]?.evaluationType,
    billingComments: formValues?.headerResult?.[0]?.billingComments,
    productEvaForm: [
      {
        formIndex: null,
        privateBrandProduct,
        competitiveBrand,
        organolepticEvaluationSummary: generateParamsForEachTable(
          organolepticSummaryInitial
        ),
        allergenDetails: [
          ...Object.values(
            generateParamsForEachTable(formValues?.allergenDetails)
          ),
        ],
        analyticalTestingProduct: {
          analyticalTestingProductDetails: [
            ...Object.values(
              generateParamsForEachTable(formValues?.analyticalTestingProduct)
            ),
          ],
        },
        microbiologicalTesting: generateParamsForEachTable(
          formValues?.microbiologicalTesting
        ),
        qaEvaluationFormAttachment: {
          ...transformProductImageryParams(formValues?.productImagery?.[0]),
        },
      },
    ],
    dietaryCertification: generateParamsForEachTable(
      formValues?.dietaryCertification
    ),
    physicalPackagingEvaluation: transformFormValuesPhysicalPackagingTable(
      generateParamsForEachTable(formValues?.physicalPackagingEvaluation)
    ),
    sampleInformation: generateParamsForEachTable(
      formValues?.sampleInformation
    ),
    statement: generateParamsForEachTable(formValues?.statement),
  };

  return result;
};

const transformParamsEvalNonFoodForm = (formValues) => {
  let result = {};

  const { privateBrandProduct, competitiveBrandProduct: competitiveBrand } =
    generateBrandTypeProduct(formValues?.headerTable, formValues?.headerImage);

  result = {
    ...result,
    overallResult: formValues?.headerResult?.[0]?.overallResult,
    majorNonConformances: formValues?.headerResult?.[0]?.majorNonConformances,
    reviewer: formValues?.headerResult?.[0]?.reviewer,
    evaluationType: formValues?.headerResult?.[0]?.evaluationType,
    billingComments: formValues?.headerResult?.[0]?.billingComments,
    productEvaForm: [
      {
        formIndex: null,
        privateBrandProduct,
        competitiveBrand,

        allergenDetails: [
          ...Object.values(
            generateParamsForEachTable(formValues?.allergenDetails)
          ),
        ],
        analyticalTestingProduct: {
          analyticalTestingProductDetails: [
            ...Object.values(
              generateParamsForEachTable(formValues?.analyticalTestingProduct)
            ),
          ],
        },
        microbiologicalTesting: generateParamsForEachTable(
          formValues?.microbiologicalTesting
        ),
        qaEvaluationFormAttachment: {
          ...transformProductImageryParams(formValues?.productImagery?.[0]),
        },
        sensoryEvaluationSummary: generateParamsForEachTable(
          formValues?.sensoryEvaluationSummary
        ),
      },
    ],
    sampleInformation: generateParamsForEachTable(
      formValues?.sampleInformation
    ),
    physicalPackagingEvaluation: transformFormValuesPhysicalPackagingTable(
      generateParamsForEachTable(formValues?.physicalPackagingEvaluation)
    ),
  };

  return result;
};

// function getProductEvaForm(formValues) {
//   if (!Array.isArray(formValues?.productEvalForms)) return [];

//   return formValues.productEvalForms.map((form, index) => ({
//     formIndex: index, // or form.formIndex if backend expects it
//     privateBrandProduct: form.privateBrandProduct,
//     competitiveBrand: form.competitiveBrand,
//     organolepticEvaluationSummary: generateParamsForEachTable(
//       form.organolepticEvaluationSummary
//     ),
//     allergenDetails: [
//       ...Object.values(
//         generateParamsForEachTable(form.allergenDetails)
//       ),
//     ],
//     analyticalTestingProduct: {
//       analyticalTestingProductDetails: [
//         ...Object.values(
//           generateParamsForEachTable(form.analyticalTestingProduct)
//         ),
//       ],
//     },
//     microbiologicalTesting: generateParamsForEachTable(
//       form.microbiologicalTesting
//     ),
//     qaEvaluationFormAttachment: {
//       ...transformProductImageryParams(form.productImagery?.[0]),
//     },
//   }));
// }

function getProductEvaForm(formValues) {
  if (!Array.isArray(formValues?.productEvalForms)) return [];
  let result = [];
  formValues.productEvalForms.forEach((form, index) => {
    // Ensure title is included in organolepticEvaluationSummary for posting

    let organolepticEvaluationSummaryRaw = formValues.productEvalForms[index].organolepticEvaluationSummary;
    let organolepticEvaluationSummary = transformFormValuesMultiPackTitle(organolepticEvaluationSummaryRaw);
    if (organolepticEvaluationSummaryRaw && organolepticEvaluationSummaryRaw.title !== undefined) {
      organolepticEvaluationSummary = {
        ...organolepticEvaluationSummary,
        title: organolepticEvaluationSummaryRaw.title
      };
    }
    const microbiologicalTesting = formValues.productEvalForms[index].microbiologicalTesting;
    const allergenDetails = formValues.productEvalForms[index].allergenDetails;
    const analyticalTestingProduct = formValues.productEvalForms[index].analyticalTestingProduct;
    var qaEvaluationFormAttachment = formValues.productEvalForms[index].qaEvaluationFormAttachment;


    const productEvaForm = {
      formIndex: index, // or form.formIndex if backend expects it
      privateBrandProduct: form.privateBrandProduct,
      competitiveBrand: form.competitiveBrand,
      organolepticEvaluationSummary: organolepticEvaluationSummary,
      allergenDetails: allergenDetails,
      analyticalTestingProduct: analyticalTestingProduct,
      microbiologicalTesting: microbiologicalTesting,
      qaEvaluationFormAttachment: qaEvaluationFormAttachment,
      // Add any other fields that are needed
    };
    result.push(productEvaForm);
  }
  );

  return result;
}

const transformParamsEvalMultiPackFoodForm = (formValues) => {
  // This function transforms the form values to match the structure received from getQaEvaluationFormMultiPackDetail
  // The output should include productEvalForms (array), and all other top-level fields as in the API response
  let result = {};

  // Map headerResult fields to top-level
  const headerResult = formValues?.headerResult?.[0] || {};
  result = {
    ...result,
    overallResult: headerResult.overallResult,
    majorNonConformances: headerResult.majorNonConformances,
    reviewer: headerResult.reviewer,
    evaluationType: headerResult.evaluationType,
    billingComments: headerResult.billingComments,
  };

  let productEvalForms = formValues?.productEvalForms || [];
  if (!Array.isArray(productEvalForms)) {
    productEvalForms = [...productEvalForms];
  }

  let productEvalFormsArray = [];


  formValues.productEvalForms.forEach((form, index) => {
    // Find the row with property.name === 'qaEvalOrganolepticFinding'

    let organolepticSummaryInitial = JSON.parse(JSON.stringify(formValues.organolepticEvaluationSummary[index]));
    organolepticSummaryInitial = updateOrganolepticSummaryScores(organolepticSummaryInitial);

    let organolepticEvaluationSummary = generateParamsForEachTable(organolepticSummaryInitial);

    const microbiologicalTesting = generateParamsForEachTable(formValues.microbiologicalTesting[index]);
    const allergenDetails = generateParamsForEachTable(formValues.allergenDetails[index]);
    let analyticalTestingProductDetails = formValues.analyticalTestingProduct[index];

    let titleRow = analyticalTestingProductDetails.find(row => row.property && row.property.name === 'title');
    var sectionTitle = titleRow?.value;
    // Filter out rows where property.name === 'title'
    analyticalTestingProductDetails = Array.isArray(analyticalTestingProductDetails)
      ? (analyticalTestingProductDetails.filter(row => !(row.property && row.property.name === 'title')))
      : analyticalTestingProductDetails;

    const analyticalTestingProduct = { title: sectionTitle, analyticalTestingProductDetails: analyticalTestingProductDetails };
    var qaEvaluationFormAttachment = transformProductImageryParams(formValues?.productImagery?.[0])
    const { privateBrandProduct, competitiveBrandProduct: competitiveBrand } = generateBrandTypeProduct(formValues?.headerTable[index], formValues?.headerImage[index]);
    // const privateBrandProduct = form.privateBrandProduct;
    // const competitiveBrand = form.competitiveBrand;  

    const productEvaForm = {
      privateBrandProduct: privateBrandProduct,
      competitiveBrand: competitiveBrand,
      privateBrandProductId: form.privateBrandProductId,
      organolepticEvaluationSummary: organolepticEvaluationSummary,
      allergenPbCbProduct: allergenDetails,
      analyticalTestingProduct: analyticalTestingProduct,
      microbiologicalTesting: microbiologicalTesting,
      qaEvaluationFormAttachment: qaEvaluationFormAttachment,
      // Add any other fields that are needed
    }
    console.log(
      'MULTIPACK PRODUCT',
      index,
      productEvaForm.privateBrandProduct,
      productEvaForm.competitiveBrand
    );
    
    productEvalFormsArray.push(productEvaForm);
  });

  productEvalForms = [...productEvalFormsArray];

  // Map productEvalForms (array of productEvaForm)
  if (Array.isArray(productEvalForms) && productEvalForms.length > 0) {
    result.productEvaForm = productEvalForms.map((form, index) => {
      return {
        ...form,
        formIndex: index + 1,
      };
    });
  } else {
    result.productEvaForm = [];
  }


  // Map other top-level tables if present
  if (formValues?.dietaryCertification) {
    //result.dietaryCertification = processMultiPackDietaryCertification(formValues.dietaryCertification);
    result.dietaryCertification = generateParamsForEachTable(processMultiPackDietaryCertification(formValues.dietaryCertification));
  }

  if (formValues?.physicalPackagingEvaluation) {
    result.physicalPackagingEvaluation = transformFormValuesPhysicalPackagingTable(generateParamsForEachTable(formValues.physicalPackagingEvaluation));
  }
  if (formValues?.sampleInformation) {
    result.sampleInformation = generateParamsForEachTable(processMultiPackSampleInformation(formValues.sampleInformation));
  }
  if (formValues?.statement) {
    result.statement = generateParamsForEachTable(processMultiPackStatement(formValues.statement));
  }

  // Add any other fields as needed to match the API response

  return result;
};

export const transformFormParamsEvalForm = (formValues, type) => {
  if (isFoodForm(type)) return transformParamsEvalFoodForm(formValues);

  if (isNonFoodForm(type)) return transformParamsEvalNonFoodForm(formValues);

  if (isMultiPackFoodForm(type)) return transformParamsEvalMultiPackFoodForm(formValues);

  return {};
};

const transformInitialPhysicalPackagingTable = (dataTable) => {
  let result = {};

  if (dataTable) {
    for (const [key, value] of Object.entries(dataTable)) {
      // countryOfOrigin is array, so we need to transform
      if (key === COUNTRY_OF_ORIGIN && !isEmpty(value)) {
        const mappingValues = value.reduce((acc, item, index) => {
          acc[`${key}${index + 1}`] = { ...item };
          return acc;
        }, {});
        result = {
          ...result,
          ...mappingValues,
        };
      } else {
        result = {
          ...result,
          [key]: value,
        };
      }
    }
  }

  return result;
};

const transformFormValuesMultiPackTitle = (formValues) => {
  let title = '';
  let result = { ...formValues };

  if (formValues && formValues.title && !isEmpty(formValues.title)) {

    const obj = formValues.title;
    const str = Object.keys(obj)
      .filter(k => !isNaN(k))           // take only numeric keys
      .sort((a, b) => a - b)            // sort them in order
      .map(k => obj[k])                 // get values
      .join("");                        // join into a string


    title = str;
  } else {
    result = {
      ...result,
      [key]: value,
    };

  }

  return {
    ...result,
    title: title,
  };
};


// countryOfOrigin = {}, {} -> [{}, {}]
const transformFormValuesPhysicalPackagingTable = (formValues) => {
  let countryOfOrigin = [];
  let result = {};

  if (formValues) {
    for (const [key, value] of Object.entries(formValues)) {
      // countryOfOrigin is array, so we need to transform
      if (key.includes(COUNTRY_OF_ORIGIN)) {
        countryOfOrigin.push(value);
      } else {
        result = {
          ...result,
          [key]: value,
        };
      }
    }
  }

  return {
    ...result,
    countryOfOrigin,
  };
};

const processMultiPackDietaryCertification = (dietaryCertification) => {

  if (!Array.isArray(dietaryCertification)) {
    dietaryCertification = [...dietaryCertification];
  }
  // Collect all competitiveBrandProductsN fields and merge into competitiveBrandProducts array
  let dietaryCertArr = Array.isArray(dietaryCertification)
    ? [...dietaryCertification]
    : [];

  for (var i = 0; i < dietaryCertArr.length; i++) {
    var record = dietaryCertArr[i];
    var temp = [...record.competitiveBrandProducts];
    Object.keys(record).forEach((key) => {

      const match = key.match(/^competitiveBrandProducts(\d+)$/);
      if (match) {
        const idx = Number(match[1]);
        if (!Array.isArray(record.competitiveBrandProducts)) {
          record.competitiveBrandProducts = [];
        }

        if (record[key] !== undefined) {
          temp[idx] = record[key];
        }
        else {
          temp[idx] = null;
        }
      }
    });
    dietaryCertArr[i] = { ...record, competitiveBrandProducts: [...temp] };

  }

  return dietaryCertArr;
}

const processMultiPackSampleInformation = (sampleInformation) => {

  if (!Array.isArray(sampleInformation)) {
    sampleInformation = [...sampleInformation];
  }
  // Collect all competitiveBrandProductsN fields and merge into competitiveBrandProducts array
  let sampleInformationArr = Array.isArray(sampleInformation)
    ? [...sampleInformation]
    : [];

  for (var i = 0; i < sampleInformationArr.length; i++) {
    var record = sampleInformationArr[i];
    var temp = [...record.competitiveBrandProducts];
    Object.keys(record).forEach((key) => {

      const match = key.match(/^competitiveBrandProducts(\d+)$/);
      if (match) {
        const idx = Number(match[1]);
        if (!Array.isArray(record.competitiveBrandProducts)) {
          record.competitiveBrandProducts = [];
        }

        if (record[key] !== undefined) {
          temp[idx] = record[key];
        }
        else {
          temp[idx] = null;
        }
      }
    });
    sampleInformationArr[i] = { ...record, competitiveBrandProducts: [...temp] };

  }

  return sampleInformationArr;
}

const processMultiPackStatement = (statement) => {

  if (!Array.isArray(statement)) {
    statement = [...statement];
  }
  // Collect all competitiveBrandProductsN fields and merge into competitiveBrandProducts array
  let statementArr = Array.isArray(statement)
    ? [...statement]
    : [];

  for (var i = 0; i < statementArr.length; i++) {
    var record = statementArr[i];
    var temp = [...record.competitiveBrandProducts];
    Object.keys(record).forEach((key) => {

      const match = key.match(/^competitiveBrandProducts(\d+)$/);
      if (match) {
        const idx = Number(match[1]);
        if (!Array.isArray(record.competitiveBrandProducts)) {
          record.competitiveBrandProducts = [];
        }

        if (record[key] !== undefined) {
          temp[idx] = record[key];
        }
        else {
          temp[idx] = null;
        }
      }
    });
    statementArr[i] = { ...record, competitiveBrandProducts: [...temp] };

  }

  return statementArr;
}

function updateOrganolepticSummaryScores(organolepticSummaryInitial) {
  if (!Array.isArray(organolepticSummaryInitial)) return organolepticSummaryInitial;
  const qaEvalRow = organolepticSummaryInitial.find(row => row.property && row.property.name === 'qaEvalOrganolepticFinding');
  let appearanceVisualRow = organolepticSummaryInitial.find(row => row.property && row.property.name === 'appearanceVisual');
  if (appearanceVisualRow && qaEvalRow) {
    appearanceVisualRow.privateBrandProductScores = qaEvalRow['apperanceVisualPrivateBrandScore'];
    appearanceVisualRow.competitiveBrandProductScores = qaEvalRow['apperanceVisualCompetitiveBrandScore'];
    const idx = organolepticSummaryInitial.indexOf(appearanceVisualRow);
    if (idx !== -1) {
      organolepticSummaryInitial[idx] = appearanceVisualRow;
    }
  }

  let aromaOdorRow = organolepticSummaryInitial.find(row => row.property && row.property.name === 'aromaOdor');
  if (aromaOdorRow && qaEvalRow) {
    aromaOdorRow.privateBrandProductScores = qaEvalRow['aromaOdorPrivateBrandScore'];
    aromaOdorRow.competitiveBrandProductScores = qaEvalRow['aromaOdorCompetitiveBrandScore'];
    const idx = organolepticSummaryInitial.indexOf(aromaOdorRow);
    if (idx !== -1) {
      organolepticSummaryInitial[idx] = aromaOdorRow;
    }
  }

  let flavorTasteRow = organolepticSummaryInitial.find(row => row.property && row.property.name === 'flavorTaste');
  if (flavorTasteRow && qaEvalRow) {
    flavorTasteRow.privateBrandProductScores = qaEvalRow['flavorTastePrivateBrandScore'];
    flavorTasteRow.competitiveBrandProductScores = qaEvalRow['flavorTasteCompetitiveBrandScore'];
    const idx = organolepticSummaryInitial.indexOf(flavorTasteRow);
    if (idx !== -1) {
      organolepticSummaryInitial[idx] = flavorTasteRow;
    }
  }

  let textureMouthfeelRow = organolepticSummaryInitial.find(row => row.property && row.property.name === 'textureMouthfeel');
  if (textureMouthfeelRow && qaEvalRow) {
    textureMouthfeelRow.privateBrandProductScores = qaEvalRow['textureMouthfeelPrivateBrandScore'];
    textureMouthfeelRow.competitiveBrandProductScores = qaEvalRow['textureMouthfeelCompetitiveBrandScore'];
    const idx = organolepticSummaryInitial.indexOf(textureMouthfeelRow);
    if (idx !== -1) {
      organolepticSummaryInitial[idx] = textureMouthfeelRow;
    }
  }

  let overallRow = organolepticSummaryInitial.find(row => row.property && row.property.name === 'overall');
  if (overallRow && qaEvalRow) {
    overallRow.privateBrandProductScores = qaEvalRow['overallPrivateBrandScore'];
    overallRow.competitiveBrandProductScores = qaEvalRow['overallCompetitiveBrandScore'];
    const idx = organolepticSummaryInitial.indexOf(overallRow);
    if (idx !== -1) {
      organolepticSummaryInitial[idx] = overallRow;
    }
  }
  return organolepticSummaryInitial;
}
